import { reactive } from 'vue';

const state = reactive({
  notifications: []
});

/**
 * ███ `persist: true` — A MESSAGE THAT MUST NOT AUTO-DISMISS ███
 *
 * Added 2026-10-18 for the Xero exported-period warning. The default toast
 * auto-dismisses after 4 seconds (NotificationPopup's DURATION), which is right
 * for "Timesheet approved" and wrong for a payroll-correctness message:
 *
 *  - The warning arrives AFTER the write, so there is no second chance. The
 *    approval has already happened; the only thing the message can do is tell
 *    somebody they have work to do outside Rosterem.
 *  - Four seconds of a manager not looking at the screen and it is gone with no
 *    trace. A warning nobody reads is the silent fail-open this feature exists
 *    to remove, wearing a different hat.
 *
 * So a persistent notification stays until the person dismisses it, and the
 * five-item cap below will not evict one.
 */
const addNotification = (message, status, options = {}) => {
  // Dedupe against whatever's currently showing — repeats of the same
  // message/status bump a count instead of stacking a new toast per call.
  const existing = state.notifications.find(
    (n) => n.message === message && n.status === status
  );
  if (existing) {
    existing.count += 1;
    return;
  }

  state.notifications.push({
    id: String(Date.now()),
    message,
    status,
    count: 1,
    persist: options.persist === true,
  });
  if (state.notifications.length > 5) {
    // ⚠ EVICT THE OLDEST NON-PERSISTENT ONE, NOT THE OLDEST. A plain shift()
    // would drop a payroll warning to make room for "Timesheet approved" —
    // five routine toasts after a bulk run would silently evict the one
    // message that mattered. If every one of them is persistent, keep them
    // all: that is five things somebody has to deal with, not clutter.
    const evictable = state.notifications.findIndex((n) => !n.persist);
    if (evictable !== -1) state.notifications.splice(evictable, 1);
  }
};

const removeNotification = (id) => {
  const index = state.notifications.findIndex(notification => notification.id === id);
  if (index !== -1) {
    state.notifications.splice(index, 1);
  }
};

export default {
  state,
  addNotification,
  removeNotification
};
